import { useRef, useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { updateFormField } from "../../../../store/schedulingReducer";
import useInterviewList from "../../../hooks/useInterviewList";
import SvgMapper from "../../../features/common/SvgMapper";

const SEARCH_DEBOUNCE_MS = 350;

const SelectInteview = () => {
  const dispatch = useDispatch();
  const { interviewType } = useSelector((state) => state.scheduling);
  const { interviewList, loadMore, hasMore, loading, fetchInterviewList } = useInterviewList({ status: "ACTIVE" });

  const scrollRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const fetchInterviewListRef = useRef(fetchInterviewList);
  const wasDropdownOpenRef = useRef(false);
  fetchInterviewListRef.current = fetchInterviewList;

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (!isDropdownOpen) {
      wasDropdownOpenRef.current = false;
      return;
    }
    const justOpened = !wasDropdownOpenRef.current;
    wasDropdownOpenRef.current = true;

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    if (justOpened && !searchQuery.trim()) return;

    const query = searchQuery.trim();
    const timer = setTimeout(() => {
      searchDebounceRef.current = null;
      const filters = query ? { interview_name: query } : {};
      fetchInterviewListRef.current(1, false, filters);
    }, SEARCH_DEBOUNCE_MS);
    searchDebounceRef.current = timer;
    return () => {
      clearTimeout(timer);
      if (searchDebounceRef.current === timer) searchDebounceRef.current = null;
    };
  }, [searchQuery, isDropdownOpen]);

  useEffect(() => {
    if (!isDropdownOpen) {
      setSearchQuery("");
    } else {
      searchInputRef.current?.focus();
    }
  }, [isDropdownOpen]);

  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollTop + clientHeight >= scrollHeight - 10 && hasMore && !loading) {
      loadMore();
    }
  }, [hasMore, loading, loadMore]);

  useEffect(() => {
    const container = scrollRef.current;
    if (isDropdownOpen && container) {
      container.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [isDropdownOpen, handleScroll]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const list = Array.isArray(interviewList) ? interviewList : [];

  return (
    <>
      <div>
        <label className="block mb-2 text-sm font-medium text-gray-700">
          Interview Type
        </label>
        <div className="relative" ref={dropdownRef}>
          <div
            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg bg-white cursor-pointer focus:ring-2 focus:ring-blue-400 flex items-center min-h-[2.5rem]"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            {SvgMapper.dropdown(
              `w-5 h-5 text-black-400 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none ${
                isDropdownOpen ? "rotate-180" : ""
              }`
            )}
            {isDropdownOpen ? (
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search by interview name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="flex-1 min-w-0 border-0 bg-transparent p-0 text-sm outline-none cursor-text"
              />
            ) : (
              <span className="flex-1 min-w-0 text-left">
                {interviewType || "Choose interview type..."}
              </span>
            )}
          </div>

          {isDropdownOpen && (
            <div
              ref={scrollRef}
              className="absolute z-10 w-full mt-0.5 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto"
            >
              {list.map((interview) => {
                const name = interview?.interview_name || "Unknown";
                return (
                  <div
                    key={interview?.id || name}
                    className="px-3 py-2 cursor-pointer hover:bg-blue-100"
                    onClick={() => {
                      dispatch(updateFormField({ key: "interviewType", value: name }));
                      dispatch(updateFormField({ key: "interviewTypeId", value: interview?.id || "" }));
                      setIsDropdownOpen(false);
                    }}
                  >
                    {name}
                  </div>
                );
              })}
              {list.length === 0 && !loading && (
                <div className="px-3 py-2 text-center text-sm text-gray-500">
                  {searchQuery.trim() ? "No interviews match your search" : "No interviews"}
                </div>
              )}
              {loading && (
                <div className="px-3 py-2 text-center text-sm text-gray-500">
                  Loading more...
                </div>
              )}
              {!hasMore && !loading && list.length > 0 && (
                <div className="px-3 py-2 text-center text-xs text-gray-400">
                  No more interviews...
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SelectInteview;
